/* eslint-disable @typescript-eslint/no-explicit-any */
import { type FormEvent, useCallback, useState } from "react";
import { type AxiosResponse } from "axios";
import { handleAxiosError, type ErrorResponse } from "../common/error_handling";

type UseSubmitFormProps<T = any> = {
  submitMethod: () => Promise<AxiosResponse<T>>;
  runAfterSuccess?: (data: T) => any;
  runAfterError?: (error: ErrorResponse) => any;
};

export type UseSubmitFormErrors<T> =
  | {
      [key in keyof T]?: string[];
    }
  | null;

type UseSubmitForm<T> = {
  success: boolean;
  errors: UseSubmitFormErrors<T>;
  isLoading: boolean;
  submitHandler: (e?: FormEvent) => Promise<void>;
  clearErrors: () => void;
  setErrors: (errors: UseSubmitFormErrors<T>) => void;
};

export function useSubmitForm<FormValues, ResponseType = any>({
  submitMethod,
  runAfterSuccess,
  runAfterError,
}: UseSubmitFormProps<ResponseType>): UseSubmitForm<FormValues> {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<
    { [key in keyof FormValues]?: string[] } | null
  >(null);

  const submitHandler = async (e?: FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    setIsLoading(true);

    try {
      const response = await submitMethod();

      setSuccess(true);
      setErrors(null);

      await runAfterSuccess?.(response.data);
    } catch (err) {
      //TODO: ADD this
      const errorResponse = handleAxiosError(err);
      // setErrors(handleAxiosError(err).errors);
      setSuccess(false);

      // Wrap runAfterError in try-catch to ensure isLoading is always reset
      try {
        runAfterError?.(errorResponse);
      } catch (errorHandlerErr) {
        // Log error handler errors but don't prevent loading state reset
        console.error("Error in runAfterError handler:", errorHandlerErr);
      }
    } finally {
      // Always reset loading state, even if error handlers throw
      setIsLoading(false);
    }
  };

  const clearErrors = useCallback(() => {
    setErrors(null);
  }, []);

  return {
    submitHandler,
    isLoading,
    success,
    errors,
    setErrors,
    clearErrors,
  };
}
